import { BACKEND_SERVER_NINJA } from '@/settings';
import axios from 'axios';

let cachedCsrfToken: string | null = null;
let inFlightRequest: Promise<string> | null = null;

export const getCachedNinjaCsrf = (): string | null => cachedCsrfToken;

export const setCachedNinjaCsrf = (token: string | null): void => {
  cachedCsrfToken = token;
  if (token) {
    axios.defaults.headers.common['X-CSRFTOKEN'] = token;
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `csrftoken=${token}; Path=/; SameSite=Lax${secure}`;
    } catch (_e) {
      // ignore if not in browser
    }
  } else {
    delete axios.defaults.headers.common['X-CSRFTOKEN'];
    try {
      // expire cookie
      document.cookie = 'csrftoken=; Max-Age=0; Path=/; SameSite=Lax';
    } catch (_e) {}
  }
};

export const fetchNinjaCsrf = async (): Promise<string> => {
  if (cachedCsrfToken) return cachedCsrfToken;
  if (inFlightRequest) return inFlightRequest;

  inFlightRequest = (async () => {
    const response = await fetch(`${BACKEND_SERVER_NINJA}/auth/csrf`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to prefetch CSRF: ${response.status}`);
    }
    const json: { csrf_token?: string } = await response.json();
    const token = json.csrf_token ?? '';
    setCachedNinjaCsrf(token);
    try {
      // Ensure CSRF cookie exists for current host so it will be sent with requests
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `csrftoken=${token}; Path=/; SameSite=Lax${secure}`;
    } catch (_e) {
      // non-browser or cookie write failed; rely on server-set cookie
    }
    inFlightRequest = null;
    return token;
  })();

  try {
    return await inFlightRequest;
  } finally {
    inFlightRequest = null;
  }
};

export const prefetchNinjaCsrf = async (): Promise<void> => {
  try {
    await fetchNinjaCsrf();
  } catch (e) {
    // Non-fatal: we will still be able to fetch lazily later if needed
    // eslint-disable-next-line no-console
    console.warn('[CSRF] Prefetch failed', e);
  }
};

export const installNinjaCsrfInterceptors = (): void => {
  // Retry once on CSRF failure after refreshing token
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      try {
        const originalRequest = error.config as { _csrfRetry?: boolean } & typeof error.config;
        const status = error?.response?.status;
        const data = error?.response?.data as
          | { error?: { code?: string; message?: string } | string; code?: string }
          | undefined;
        const code = (typeof data?.error === 'string' ? undefined : data?.error?.code) || data?.code;
        const isCsrfFailed = status === 403 && code === 'CSRF_FAILED';
        const isNinjaUrl = typeof originalRequest?.url === 'string' && originalRequest.url.startsWith(BACKEND_SERVER_NINJA);

        if (isCsrfFailed && isNinjaUrl && !originalRequest?._csrfRetry) {
          originalRequest._csrfRetry = true;
          const newToken = await fetchNinjaCsrf();
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            'X-CSRFTOKEN': newToken,
          } as any;
          return axios(originalRequest);
        }
      } catch (_e) {
        // fallthrough to reject
      }
      return Promise.reject(error);
    }
  );
};


